import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography
} from '@mui/material'
import React, { useCallback, useRef, useState } from 'react'
import ReactCrop, {
  Crop,
  PixelCrop,
  centerCrop,
  convertToPixelCrop,
  makeAspectCrop
} from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'
import { useTranslation } from 'react-i18next'

type Props = {
  open: boolean
  imageSrc: string
  onClose: () => void
  onCropped: (file: File) => void
}

const fetchAsBlobUrl = async (src: string): Promise<string | null> => {
  try {
    // cache: 'no-store' bypasses the browser cache so we always get a fresh
    // response with CORS headers, even if an older non-CORS response was cached.
    const res = await fetch(src, {
      credentials: 'include',
      mode: 'cors',
      cache: 'no-store',
    })
    if (!res.ok) return null
    const blob = await res.blob()
    return URL.createObjectURL(blob)
  } catch {
    return null
  }
}

const cropToFile = async (displayImg: HTMLImageElement, pixelCrop: PixelCrop): Promise<File> => {
  const scaleX = displayImg.naturalWidth / displayImg.width
  const scaleY = displayImg.naturalHeight / displayImg.height
  const naturalW = Math.round(pixelCrop.width * scaleX)
  const naturalH = Math.round(pixelCrop.height * scaleY)

  const blobUrl = await fetchAsBlobUrl(displayImg.src)
  const canvasSrc = blobUrl ?? displayImg.src

  const canvasImage = new Image()
  await new Promise<void>((resolve, reject) => {
    canvasImage.onload = () => resolve()
    canvasImage.onerror = reject
    canvasImage.src = canvasSrc
  })

  const canvas = document.createElement('canvas')
  canvas.width = naturalW
  canvas.height = naturalH
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Could not get canvas context')

  ctx.drawImage(
    canvasImage,
    pixelCrop.x * scaleX,
    pixelCrop.y * scaleY,
    naturalW,
    naturalH,
    0,
    0,
    naturalW,
    naturalH
  )

  return new Promise<File>((resolve, reject) => {
    try {
      canvas.toBlob((blob) => {
        if (blobUrl) URL.revokeObjectURL(blobUrl)
        if (!blob) { reject(new Error('Canvas is empty')); return }
        resolve(new File([blob], 'cropped.jpg', { type: 'image/jpeg' }))
      }, 'image/jpeg', 0.92)
    } catch (e) {
      if (blobUrl) URL.revokeObjectURL(blobUrl)
      reject(e)
    }
  })
}

const getNaturalDims = (img: HTMLImageElement, crop: PixelCrop) => ({
  width: Math.round(crop.width * (img.naturalWidth / img.width)),
  height: Math.round(crop.height * (img.naturalHeight / img.height))
})

export const ImageCropDialog: React.FC<Props> = ({ open, imageSrc, onClose, onCropped }) => {
  const { t } = useTranslation()
  const imgRef = useRef<HTMLImageElement>(null)

  const [crop, setCrop] = useState<Crop>()
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>()
  const [widthInput, setWidthInput] = useState<string>('')
  const [heightInput, setHeightInput] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const pixelDimensions = completedCrop && imgRef.current
    ? getNaturalDims(imgRef.current, completedCrop)
    : null

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget
    const initialCrop = centerCrop(
      makeAspectCrop(
        { unit: '%', width: 80 },
        img.naturalWidth / img.naturalHeight,
        img.width,
        img.height
      ),
      img.width,
      img.height
    )
    setCrop(initialCrop)
    const px = convertToPixelCrop(initialCrop, img.width, img.height)
    setCompletedCrop(px)
    const dims = getNaturalDims(img, px)
    setWidthInput(String(dims.width))
    setHeightInput(String(dims.height))
  }, [])

  /** Applies a manually typed dimension, adjusting the crop frame size. */
  const applyDimension = (axis: 'width' | 'height', rawValue: string) => {
    const val = parseInt(rawValue, 10)
    if (isNaN(val) || val <= 0 || !imgRef.current || !completedCrop) return

    const img = imgRef.current
    const scaleX = img.naturalWidth / img.width
    const scaleY = img.naturalHeight / img.height

    if (axis === 'width') {
      const displayW = Math.min(val / scaleX, img.width)
      const x = Math.max(0, Math.min(completedCrop.x, img.width - displayW))
      const next: PixelCrop = { ...completedCrop, x, width: displayW, unit: 'px' }
      setCompletedCrop(next)
      setCrop(next)
      setWidthInput(String(Math.round(displayW * scaleX)))
    } else {
      const displayH = Math.min(val / scaleY, img.height)
      const y = Math.max(0, Math.min(completedCrop.y, img.height - displayH))
      const next: PixelCrop = { ...completedCrop, y, height: displayH, unit: 'px' }
      setCompletedCrop(next)
      setCrop(next)
      setHeightInput(String(Math.round(displayH * scaleY)))
    }
  }

  const handleConfirm = async () => {
    if (!completedCrop || !imgRef.current) return

    try {
      setLoading(true)
      setError(null)
      const file = await cropToFile(imgRef.current, completedCrop)
      onCropped(file)
      onClose()
    } catch (e: any) {
      if (e?.name === 'SecurityError') {
        setError(t('errors.cropCors', {
          defaultValue: 'Zuschneiden nicht möglich: Das Bild hat keine CORS-Berechtigung. Bitte laden Sie zunächst ein neues Bild hoch.'
        }))
      } else {
        setError(t('errors.cropFailed', { defaultValue: 'Zuschneiden fehlgeschlagen. Bitte erneut versuchen.' }))
      }
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setCrop(undefined)
    setCompletedCrop(undefined)
    setError(null)
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{t('edit.poi.cropImage', { defaultValue: 'Bild zuschneiden' })}</DialogTitle>

        <DialogContent>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="flex-start"
            sx={{ backgroundColor: '#1a1a1a', borderRadius: 1, p: 2, minHeight: 200 }}
          >
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => {
                setCompletedCrop(c)
                if (imgRef.current) {
                  const dims = getNaturalDims(imgRef.current, c)
                  setWidthInput(String(dims.width))
                  setHeightInput(String(dims.height))
                }
              }}
              ruleOfThirds
            >
              <img
                ref={imgRef}
                src={imageSrc}
                onLoad={onImageLoad}
                style={{ maxHeight: '55vh', maxWidth: '100%', display: 'block' }}
                alt="crop-source"
              />
            </ReactCrop>
          </Box>

          {/* Dimension inputs */}
          <Box mt={2} display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <TextField
              label={t('edit.poi.cropWidth', { defaultValue: 'Breite' })}
              size="small"
              value={widthInput}
              onChange={(e) => setWidthInput(e.target.value)}
              onBlur={(e) => applyDimension('width', e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') applyDimension('width', widthInput) }}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', min: 1 }}
              InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
              sx={{ width: 130 }}
              disabled={!completedCrop}
            />
            <Typography variant="body2" color="text.disabled">×</Typography>
            <TextField
              label={t('edit.poi.cropHeight', { defaultValue: 'Höhe' })}
              size="small"
              value={heightInput}
              onChange={(e) => setHeightInput(e.target.value)}
              onBlur={(e) => applyDimension('height', e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') applyDimension('height', heightInput) }}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', min: 1 }}
              InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
              sx={{ width: 130 }}
              disabled={!completedCrop}
            />
            {imgRef.current?.naturalWidth
              ? (
                <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
                  {t('edit.poi.cropOriginal', { defaultValue: 'Original' })}{': '}
                  {imgRef.current.naturalWidth} × {imgRef.current.naturalHeight} px
                </Typography>
                )
              : null}
          </Box>

          <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
            {t('edit.poi.cropHint', {
              defaultValue: 'Der nicht ausgewählte Bildbereich wird beim Speichern verworfen und kann nicht wiederhergestellt werden.'
            })}
          </Typography>

          {error && (
            <Typography color="error" variant="caption" display="block" mt={0.5}>
              {error}
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="secondary" disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={!completedCrop || completedCrop.width === 0 || loading}
          >
            {t('common.apply', { defaultValue: 'Übernehmen' })}
          </Button>
        </DialogActions>
    </Dialog>
  )
}
